/**
 * PowerSync Backend Connector
 * 
 * Обеспечивает связь между PowerSync клиентом и бэкендом:
 * 1. fetchCredentials — получает JWT токен для PowerSync
 * 2. uploadData — отправляет локальные мутации на сервер
 * 
 * Этот коннектор платформо-независимый и использует apiClient для всех запросов.
 */

import {
  AbstractPowerSyncDatabase,
  CrudEntry,
  PowerSyncBackendConnector,
  UpdateType,
} from '@powersync/common';

// UUID v4 pattern check
function isUUID(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

export interface ConnectorConfig {
  /** PowerSync instance URL (из Dashboard). Может быть пустой строкой — будет получен из fetchPowerSyncToken. */
  powersyncUrl: string;
  /** Функция для получения текущего auth токена */
  getToken: () => Promise<string | null>;
  /** Функция для запроса PowerSync-специфичного JWT с бэкенда. Может вернуть powersyncUrl. */
  fetchPowerSyncToken: () => Promise<{ token: string; expiresAt?: Date; powersyncUrl?: string }>;
  /** Функция для отправки мутации на бэкенд */
  uploadCrudEntry: (entry: CrudEntry) => Promise<void>;
  /** Опциональный callback для уведомления UI о перманентных ошибках (4xx) */
  onPermanentError?: (op: CrudEntry, status: number, message?: string) => void;
}

export class AlveonPowerSyncConnector implements PowerSyncBackendConnector {
  private config: ConnectorConfig;

  constructor(config: ConnectorConfig) {
    this.config = config;
  }

  /**
   * Получает credentials для подключения к PowerSync.
   * Вызывается автоматически каждые несколько минут.
   */
  async fetchCredentials() {
    try {
      const result = await this.config.fetchPowerSyncToken();
      
      // Обновляем powersyncUrl из ответа (если вернулся)
      if (result.powersyncUrl) {
        this.config.powersyncUrl = result.powersyncUrl;
      }

      return {
        endpoint: this.config.powersyncUrl,
        token: result.token,
        expiresAt: result.expiresAt,
      };
    } catch (error) {
      console.error('[PowerSync Connector] Failed to fetch credentials:', error);
      throw error;
    }
  }

  /**
   * Отправляет локальные изменения на сервер.
   * PowerSync вызывает этот метод для каждой транзакции из очереди.
   * 
   * На Этапе 1 (read-only) этот метод пока заглушка.
   * На Этапе 3 будет полноценная реализация для всех таблиц.
   */
  async uploadData(database: AbstractPowerSyncDatabase): Promise<void> {
    const transaction = await database.getNextCrudTransaction();
    
    if (!transaction) {
      return;
    }

    const opsSummary = transaction.crud.map(o => `${o.table}/${o.op}/${o.id.substring(0, 8)}`).join(', ');
    console.log(`[PowerSync Connector] uploadData: ${transaction.crud.length} ops: [${opsSummary}]`);

    // ─── Коалесценция UUID операций ───
    // Если для одного UUID есть PUT + DELETE — пропускаем оба (запись создана и удалена offline).
    // Если для одного UUID есть PUT + PATCH — мержим PATCH данные в PUT (редактирование до синхронизации).
    const opsGroupedByKey = new Map<string, CrudEntry[]>();
    for (const op of transaction.crud) {
      const key = `${op.table}:${op.id}`;
      if (!opsGroupedByKey.has(key)) opsGroupedByKey.set(key, []);
      opsGroupedByKey.get(key)!.push(op);
    }

    const skipOps = new Set<CrudEntry>();
    for (const [key, ops] of opsGroupedByKey) {
      if (!isUUID(ops[0].id)) continue; // Коалесценция только для UUID (локальных записей)

      const hasPut = ops.some(o => o.op === UpdateType.PUT);
      const hasDelete = ops.some(o => o.op === UpdateType.DELETE);
      const patchOps = ops.filter(o => o.op === UpdateType.PATCH);

      if (hasPut && hasDelete) {
        // PUT + DELETE = запись создана и удалена offline, пропускаем всё
        console.log(`[PowerSync Connector] Coalesced PUT+DELETE for ${key}, skipping both`);
        for (const op of ops) skipOps.add(op);
      } else if (hasPut && patchOps.length > 0) {
        // PUT + PATCH = мержим PATCH данные в PUT
        const putOp = ops.find(o => o.op === UpdateType.PUT)!;
        for (const patch of patchOps) {
          if (patch.opData && putOp.opData) {
            Object.assign(putOp.opData, patch.opData);
          }
          skipOps.add(patch); // Пропускаем отдельные PATCH — данные уже в PUT
        }
        console.log(`[PowerSync Connector] Coalesced ${patchOps.length} PATCH(es) into PUT for ${key}`);
      }
    }

    try {
      for (const op of transaction.crud) {
        if (skipOps.has(op)) continue;

        console.log(`[PowerSync Connector] Processing: ${op.table} op=${op.op} id=${op.id}`);
        try {
          await this.config.uploadCrudEntry(op);
        } catch (error: any) {
          const status = error?.response?.status;
          // Перманентные клиентские ошибки (4xx, кроме 401) — пропускаем операцию,
          // т.к. повторный запрос с теми же данными не поможет.
          // 401 пробрасываем — это ошибка авторизации, нужен новый токен.
          if (status && status >= 400 && status < 500 && status !== 401) {
            console.warn(
              `[PowerSync Connector] Permanent error ${status} for ${op.table}/${op.id} (op=${op.op}), skipping:`,
              error?.message || error
            );
            // Уведомляем UI если есть callback
            if (this.config.onPermanentError) {
              this.config.onPermanentError(op, status, error?.message);
            }
            continue;
          }
          // Временные ошибки (5xx, network, timeout) — прерываем, PowerSync повторит позже
          throw error;
        }
      }

      // Подтверждаем транзакцию — переходим к следующей
      await transaction.complete();
      console.log('[PowerSync Connector] Upload completed successfully');
    } catch (error) {
      console.error('[PowerSync Connector] Upload failed:', error);
      // Не вызываем transaction.complete() — PowerSync повторит позже
      throw error;
    }
  }
}
